import { normalizeInstanceUrl } from '../instance/config.ts';
import { isVideoId } from '../links/routes.ts';

/**
 * Return YouTube Dislike, as data — a third-party service estimating the real dislike count from a
 * crowd-sourced sample, since YouTube stopped exposing the real figure publicly. A different shape
 * of integration from SponsorBlock/DeArrow (`integrations.md`): there is no k-anonymity form here,
 * so a lookup always names the plain video id to the service — the same privacy statement DeArrow's
 * thumbnail fetch already carries, not the hashed-bucket one its title lookup does.
 *
 * `GET {base}/votes?videoId={id}` (measured 2026-08-03, returnyoutubedislikeapi.com, CORS open):
 * `{ id, likes, rawLikes, rawDislikes, dislikes, rating, viewCount, deleted }`. `dislikes` is the
 * service's confidence-adjusted estimate (scaled from the raw crowd-sourced sample against
 * YouTube's own real like count) — the number the reference extension shows, not `rawDislikes`.
 */

export interface ReturnYoutubeDislikeSettings {
  /** Off by default: enabling it sends the plain video id to a third-party service. */
  readonly enabled: boolean;
  /** A self-hosted server's address; `null` is the official one. */
  readonly apiUrl: string | null;
}

export const RETURN_YOUTUBE_DISLIKE_DEFAULT_API_URL = 'https://returnyoutubedislikeapi.com';

export const DEFAULT_RETURN_YOUTUBE_DISLIKE_SETTINGS: ReturnYoutubeDislikeSettings = {
  enabled: false,
  apiUrl: null,
};

/** A stored service address → a canonical base URL or `null` (the official service). */
export function normalizeReturnYoutubeDislikeApiUrl(input: unknown): string | null {
  if (typeof input !== 'string' || input.trim() === '') return null;
  const result = normalizeInstanceUrl(input);
  return result.ok ? result.value : null;
}

/** Total (R4): the input is a stored blob or a hand-edited one. */
export function normalizeReturnYoutubeDislikeSettings(input: unknown): ReturnYoutubeDislikeSettings {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return DEFAULT_RETURN_YOUTUBE_DISLIKE_SETTINGS;
  }
  const record = input as Readonly<Record<string, unknown>>;
  return {
    enabled: record['enabled'] === true,
    apiUrl: normalizeReturnYoutubeDislikeApiUrl(record['apiUrl']),
  };
}

/** `null` for a video id that is not one — the caller's guard, never sent to the network. */
export function returnYoutubeDislikeRequestUrl(
  settings: ReturnYoutubeDislikeSettings,
  videoId: string,
): string | null {
  if (!isVideoId(videoId)) return null;
  const base = settings.apiUrl ?? RETURN_YOUTUBE_DISLIKE_DEFAULT_API_URL;
  return `${base}/votes?videoId=${encodeURIComponent(videoId)}`;
}
